import { SortDefinition } from './schema';

export type ConfiguredSort = {
  id?: string;
  field: string;
  ascLabel?: string;
  descLabel?: string;
};

export type InternalSortDefinition = SortDefinition & {
  defaultDirection: 'asc' | 'desc';
  tieBreakers: string[];
  resolvedField: string;
};

/**
 * Contains serialized sort definitions plus backend-only semantics used by
 * execution and cursor validation.
 */
export type SortSnapshot = {
  definitions: SortDefinition[];
  internal: InternalSortDefinition[];
  sortablePaths: Set<string>;
  fingerprintInput: string;
};

const reservedSorts = new Set(['relevance']);
const builtIns: InternalSortDefinition[] = [
  {
    id: 'relevance',
    directions: { desc: 'Most relevant' },
    defaultDirection: 'desc',
    tieBreakers: ['kind', '_computedInfo.urn'],
    resolvedField: '_score',
  },
];

/**
 * Builds portable and valid configured sorts. Public definitions contain only
 * identifiers and direction labels; execution metadata stays internal.
 */
export function mapSortDefinitions(
  configured: ConfiguredSort[],
  sortablePaths: Iterable<string>,
): SortSnapshot {
  const allowed = new Set(sortablePaths);
  const internal = [...builtIns];
  const usedIds = new Set(internal.map(sort => sort.id));
  for (const entry of configured) {
    const id = entry.id ?? entry.field;
    if (
      reservedSorts.has(id) ||
      usedIds.has(id) ||
      !allowed.has(entry.field) ||
      (!entry.ascLabel && !entry.descLabel)
    ) {
      continue;
    }
    const directions = {
      ...(entry.ascLabel ? { asc: entry.ascLabel } : {}),
      ...(entry.descLabel ? { desc: entry.descLabel } : {}),
    };
    internal.push({
      id,
      directions,
      defaultDirection: entry.descLabel ? 'desc' : 'asc',
      tieBreakers: ['kind', '_computedInfo.urn'],
      resolvedField: entry.field,
    });
    usedIds.add(id);
  }
  const definitions = internal.map(({ id, directions }) => ({
    id,
    directions,
  }));
  const fingerprintInput = JSON.stringify({ internal });
  return { definitions, internal, sortablePaths: allowed, fingerprintInput };
}

/** Selects the internal default sort from the normalized query state. */
export function resolveDefaultSort(
  query: string,
  snapshot: SortSnapshot,
): { id: string; direction: 'asc' | 'desc' } {
  const preferredId = query.trim() ? 'relevance' : 'publishedAt';
  const definition =
    snapshot.internal.find(sort => sort.id === preferredId) ??
    snapshot.internal.find(sort => sort.id === 'relevance');
  return {
    id: definition?.id ?? 'relevance',
    direction: definition?.defaultDirection ?? 'desc',
  };
}
